import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { motion } from 'framer-motion';
import MobileLayout from '../../components/Mobile/MobileLayout';
import { GET_CATEGORY_ADS } from '../../graphql/queries';

const MobileCategory: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_CATEGORY_ADS, {
    variables: { categorySlug: slug, limit: 20 },
    skip: !slug,
  });

  const ads = data?.categoryAds || [];

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </MobileLayout>
    );
  }

  if (error) {
    return (
      <MobileLayout>
        <div className="text-center py-12 px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error loading category</h2>
          <p className="text-gray-600 text-sm">Please try again later.</p>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      {/* Category Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-6 text-white">
        <h1 className="text-2xl font-bold capitalize">
          {slug?.replace(/-/g, ' ')}
        </h1>
        <p className="text-blue-100 text-sm mt-1">{ads.length} ads found</p>
      </div>

      {/* Ads Grid */}
      {ads.length === 0 ? (
        <div className="text-center py-16 px-4">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-10 h-10 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No ads found</h2>
          <p className="text-gray-500 text-sm mb-4">
            There are no ads in this category yet.
          </p>
          <button
            onClick={() => navigate('/post-ad')}
            className="px-6 py-2.5 bg-blue-500 text-white rounded-full font-medium hover:bg-blue-600 transition-colors"
          >
            Post an Ad
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 p-4">
          {ads.map((ad: any, index: number) => (
            <motion.div
              key={ad.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => navigate(`/ad/${ad.slug}`)}
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            >
              {/* Image */}
              <div className="relative aspect-square bg-gray-200">
                {ad.primaryImage ? (
                  <img
                    src={ad.primaryImage}
                    alt={ad.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <svg
                      className="w-12 h-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-3">
                <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1.5">
                  {ad.title}
                </h3>

                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-base font-bold text-blue-600">
                    {ad.price || 'Free'}
                  </span>
                  {ad.priceType === 'negotiable' && (
                    <span className="text-xs text-gray-500">(Neg)</span>
                  )}
                </div>

                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="truncate">{ad.city}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </MobileLayout>
  );
};

export default MobileCategory;
